import React, { useState, useEffect, useRef, useCallback } from 'react';
import MonacoEditor from '@monaco-editor/react';
import { Box, Button, Typography, TextField, Chip, Alert } from '@mui/material';
import { io } from 'socket.io-client';
import SessionEndedModal from './SessionEndedModal';
import axios from '../api/axios';

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

export default function RealtimeCodeEditor({ room = 'default-room', userName = 'User', userType = 'candidate', onSocketReady, sessionId, currentCode, setCurrentCode }) {
  const [code, setCode] = useState(currentCode || '-- Start coding here...');
  const [name, setName] = useState(userName);
  const [participants, setParticipants] = useState([]);
  const [connectionError, setConnectionError] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [sessionEndedModal, setSessionEndedModal] = useState({ open: false, message: '' });
  const [sessionEnded, setSessionEnded] = useState(false);
  const socketRef = useRef();
  const isRemoteUpdate = useRef(false);
  const currentCodeRef = useRef(currentCode);
  const socketReadyCalled = useRef(false);

  // Update code when currentCode prop changes
  useEffect(() => {
    if (currentCode && currentCode !== code) {
      setCode(currentCode);
      currentCodeRef.current = currentCode;
    }
  }, [currentCode]);

  const handleEditorChange = (value) => {
      // Prevent editing if session is ended    
      if (sessionEnded) {
        return;
      }
      
      setCode(value);
      setCurrentCode(value); // Update parent component
      currentCodeRef.current = value; // Update ref with current code
      if (!isRemoteUpdate.current) {
        socketRef.current.emit('custom:event', { 
          authToken: localStorage.getItem('token'),
          event: 'codeChange', 
          channel: room, 
          data: { room, code: value } 
        });
      }
      isRemoteUpdate.current = false;            
  };


  useEffect(() => {
    // Check if session is already ended (for candidates)
    if (userType === 'candidate' && sessionEnded) {
      console.log('Session ended, showing modal...');            
      setSessionEndedModal({ 
        open: true, 
        message: 'This session has ended. You will be redirected to the main page.' 
      });
      return;
    }

    // Get authentication token (if user is interviewer/company)
    const token = localStorage.getItem('token');
    
    // Connect with authentication to the code-sharing namespace
    socketRef.current = io(`${SOCKET_SERVER_URL}/code-sharing`, {
      auth: {
        app_key: 'code-sharing-key',         
        roomCode: room,
        userName: name,
        userType: userType
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    // Connection successful
    socketRef.current.on('connect', () => {
      console.log('✅ Connected to real-time server');
      setIsConnected(true);
      setConnectionError('');
      
      // Subscribe to the room channel
      socketRef.current.emit('subscribe', {
        channel: room,
        authToken: token
      });
      
      // Notify parent component that socket is ready (only once)
      if (onSocketReady && !socketReadyCalled.current) {
        socketReadyCalled.current = true;
        onSocketReady(socketRef.current);
      }
    });

    // Connection error
    socketRef.current.on('connect_error', (error) => {
      console.error('❌ Connection error:', error.message);
      setConnectionError(error.message);
      setIsConnected(false);
    });

    // Handle subscription success
    socketRef.current.on('subscription:succeeded', (data) => {
      console.log(`📺 Successfully subscribed to channel: ${data.channel}`);
    });

    // Handle unsubscription success
    socketRef.current.on('unsubscription:succeeded', (data) => {
      console.log(`📺 Successfully unsubscribed from channel: ${data.channel}`);
    });

    // Receive code updates via custom events
    socketRef.current.on('codeChange', (eventData) => {
      console.log('codeChange event received:', eventData);
      isRemoteUpdate.current = true;
      
      // Extract the code from the eventData
      const code = eventData?.code || eventData;
      
      setCode(code);
      setCurrentCode(code);
      currentCodeRef.current = code;
      
      console.log('✅ Code updated from remote:', code);
    });

    // User online notification (new server format)
    socketRef.current.on('user:online', ({ userId, appName }) => {
      console.log(`👤 User ${userId} came online in ${appName}`);
    });

    // User offline notification (new server format)
    socketRef.current.on('user:offline', ({ userId, appName }) => {
      console.log(`👋 User ${userId} went offline from ${appName}`);
    });

    // User joined notification (legacy support)
    socketRef.current.on('userJoined', ({ userName, userType, participantCount }) => {
      console.log(`👤 ${userName} (${userType}) joined the room`);
    });

    // User left notification (legacy support)
    socketRef.current.on('userLeft', ({ userName, userType, participantCount }) => {
      console.log(`👋 ${userName} left the room`);
    });

    // Participant list update
    socketRef.current.on('participantList', ({ participants, count }) => {
      setParticipants(participants);
    });

    // Disconnection
    socketRef.current.on('disconnect', (reason) => {
      console.log('🔌 Disconnected:', reason);
      setIsConnected(false);
    });

    // Session ended by interviewer
    socketRef.current.on('sessionEnded', ({ message, endedBy }) => {
      console.log('🛑 Session ended by interviewer');
      setIsConnected(false);
      setSessionEnded(true);
      setSessionEndedModal({ open: true, message });
    });

    return () => {
      // Unsubscribe from room before disconnecting
      if (socketRef.current && isConnected) {
        socketRef.current.emit('unsubscribe', {
          channel: room,
          authToken: token
        });
      }
      
      // Save code when component unmounts (user leaves)
      if (sessionId && userType !== 'candidate' && currentCodeRef.current) {
        console.log(userType);
        console.log(sessionId);
        console.log(code);
        console.log(currentCodeRef.current);
        console.log('Saving code to database on unmount...');        
      }
      socketRef.current.disconnect();
    };
  }, [room, name, userType]);

 

  // Manual save function (can be called externally)
  // const manualSave = useCallback(() => {
  //   if (sessionId && userType === 'interviewer' && currentCodeRef.current) {
  //     saveCodeToDatabase(currentCodeRef.current);
  //   }
  // }, [sessionId, userType, saveCodeToDatabase]);

  // Expose manual save function to parent component
  // useEffect(() => {
  //   if (onSocketReady && socketRef.current && !socketReadyCalled.current) {
  //     socketReadyCalled.current = true;
  //     onSocketReady(socketRef.current, { manualSave });
  //   }
  // }, [onSocketReady, manualSave]);

  const handleSessionEndedClose = () => {
    setSessionEndedModal({ open: false, message: '' });
    // Redirect based on user type
    if (userType === 'candidate') {
      window.location.href = '/';
    } else {
      window.location.href = '/sessions';
    }
  };

  return (
    <>
      <Box sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Real-Time Code Editor
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              label={isConnected ? '🟢 Connected' : '🔴 Disconnected'}
              color={isConnected ? 'success' : 'error'}
              size="small"
              sx={{ fontSize: '0.7rem', height: '20px' }}
            />
            {participants.length > 0 && (
              <Chip
                label={`${participants.length} participant${participants.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            )}
          </Box>
        </Box>
        
        {connectionError && !sessionEndedModal.open && (
          <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
            <Typography variant="caption">Connection error: {connectionError}</Typography>
          </Alert>
        )}
        
        {participants.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Active participants:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {participants.map((p, idx) => (
                <Chip
                  key={idx}
                  label={`${p.name} (${p.type})`}
                  size="small"
                  color={p.authenticated ? 'primary' : 'default'}
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              ))}
            </Box>
          </Box>
        )}
        
        <MonacoEditor
          height="calc(100vh - 200px)"
          defaultLanguage="sql"
          value={code}
          onChange={handleEditorChange}
          theme="vs-dark"
          options={{ 
            fontSize: 14, 
            minimap: { enabled: false },
            readOnly: !isConnected || sessionEnded,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            wordWrap: 'on',
            lineNumbers: 'on',
            folding: true,
            bracketPairColorization: { enabled: true },
            guides: {
              bracketPairs: true,
              indentation: true
            }
          }}
        />
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }} color="text.secondary">
          Room: <b>{room}</b> • {isConnected ? 'Real-time sync active' : 'Disconnected from server'}
        </Typography>
      </Box>

      <SessionEndedModal
        open={sessionEndedModal.open}
        message={sessionEndedModal.message}
        onClose={handleSessionEndedClose}
      />
    </>
  );
}
